import { v7 as uuidv7 } from 'uuid';
import { create } from 'zustand';
import { devtools } from 'zustand/middleware';

import type { InviteEntryPoint } from '@/features/RenterInvite/types';

interface DirtyState {
  isDirty: boolean;
  dirtyFields: Record<string, unknown>;
}

// Ref lives outside the store to avoid re-renders when updated
const dirtyStateRef = { current: { isDirty: false, dirtyFields: {} } as DirtyState };

interface InviteLeaseDetailsDrawerState {
  isOpen: boolean;
  formKey: number;
  flowId: string | null;
  entryPoint: InviteEntryPoint | null;
  fromUrl: string | null;
  newDealEmail: string | null;
  openDrawer: (entryPoint?: InviteEntryPoint, fromUrl?: string) => void;
  openCleanForm: (entryPoint?: InviteEntryPoint, fromUrl?: string) => void;
  closeDrawer: () => void;
  setNewDealEmail: (email: string | null) => void;
  clearNewDealEmail: () => void;
  dirtyStateRef: typeof dirtyStateRef;
  isFormDirty: () => boolean;
}

export const useInviteLeaseDetailsDrawerStore = create<InviteLeaseDetailsDrawerState>()(
  devtools(
    (): InviteLeaseDetailsDrawerState => ({
      isOpen: false,
      formKey: 0,
      flowId: null,
      entryPoint: null,
      fromUrl: null,
      newDealEmail: null,
      openDrawer: (entryPoint?: InviteEntryPoint, fromUrl?: string) => {
        useInviteLeaseDetailsDrawerStore.setState((state) => {
          if (state.isOpen) {
            return state;
          }
          dirtyStateRef.current = { isDirty: false, dirtyFields: {} };
          return {
            isOpen: true,
            formKey: state.formKey + 1,
            flowId: uuidv7(),
            entryPoint: entryPoint ?? null,
            fromUrl: fromUrl ?? null,
          };
        });
      },
      openCleanForm: (entryPoint?: InviteEntryPoint, fromUrl?: string) => {
        dirtyStateRef.current = { isDirty: false, dirtyFields: {} };
        useInviteLeaseDetailsDrawerStore.setState((state) => ({
          isOpen: true,
          formKey: state.formKey + 1,
          flowId: uuidv7(),
          entryPoint: entryPoint ?? null,
          fromUrl: fromUrl ?? null,
        }));
      },
      closeDrawer: () => {
        dirtyStateRef.current = { isDirty: false, dirtyFields: {} };
        useInviteLeaseDetailsDrawerStore.setState({
          isOpen: false,
          flowId: null,
          entryPoint: null,
          fromUrl: null,
        });
      },
      setNewDealEmail: (email: string | null) => {
        useInviteLeaseDetailsDrawerStore.setState({ newDealEmail: email });
      },
      clearNewDealEmail: () => {
        useInviteLeaseDetailsDrawerStore.setState({ newDealEmail: null });
      },
      dirtyStateRef,
      isFormDirty: () => {
        const { isDirty, dirtyFields } = dirtyStateRef.current;
        // isDirty can have false positives with shouldUnregister: true (isDirty=true but dirtyFields={})
        // Only consider form dirty if isDirty AND there are actual dirty fields
        const hasDirtyFields = Object.keys(dirtyFields).length > 0;
        return isDirty && hasDirtyFields;
      },
    }),
    { name: 'invite-lease-details-drawer-store' },
  ),
);
